import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaPaperPlane, FaArrowLeft, FaImage, FaTrash, FaSpinner } from 'react-icons/fa';
import api from '../../services/api';

const CreateEditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'other',
    tags: '',
    featuredImage: '',
    metaDescription: '',
    metaKeywords: '',
    allowComments: true
  });

  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const data = await blogService.getBlog(id);
      const blog = data.blog;
      
      setFormData({
        title: blog.title || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        category: blog.category || 'other',
        tags: blog.tags?.join(', ') || '',
        featuredImage: blog.featuredImage || '',
        metaDescription: blog.metaDescription || '',
        metaKeywords: blog.metaKeywords?.join(', ') || '',
        allowComments: blog.allowComments !== false
      });
      
      if (blog.featuredImage) {
        setImagePreview(blog.featuredImage);
      }
    } catch (error) {
      toast.error('Failed to fetch blog');
      navigate('/dashboard/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      toast.error('Please select an image first');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await api.post('/upload/blog-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          featuredImage: response.data.imageUrl
        }));
        setImagePreview(response.data.imageUrl);
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, featuredImage: '' }));
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async (e, submitForApproval = false) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    try {
      if (submitForApproval) {
        setSubmitting(true);
      } else {
        setSavingDraft(true);
      }

      const blogData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        metaKeywords: formData.metaKeywords.split(',').map(k => k.trim()).filter(k => k)
      };

      if (isEditMode) {
        await blogService.updateBlog(id, blogData);
        toast.success('Blog updated successfully');
        
        if (submitForApproval) {
          await blogService.submitBlog(id);
          toast.success('Blog submitted for approval!');
        }
      } else {
        const response = await blogService.createBlog(blogData);
        toast.success('Blog created successfully');
        
        if (submitForApproval && response.blog?._id) {
          await blogService.submitBlog(response.blog._id);
          toast.success('Blog submitted for approval!');
        }
      }

      navigate('/dashboard/blogs');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save blog');
    } finally {
      setSavingDraft(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/blogs')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FaArrowLeft /> Back to My Blogs
        </button>
        <h1 className="text-3xl font-bold">{isEditMode ? 'Edit Blog' : 'Create New Blog'}</h1>
        <p className="text-gray-600 mt-2">Share your knowledge with the team</p>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
        {/* Title */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium mb-2">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
            placeholder="Enter your blog title..."
            required
          />
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium mb-2">Content *</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="15"
            placeholder="Write your blog content here... (Supports markdown)"
            required
          ></textarea>
          <p className="text-xs text-gray-500 mt-2">
            Estimated read time: {Math.ceil(formData.content.split(/\s+/).length / 200)} minutes
          </p>
        </div>

        {/* Excerpt */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium mb-2">Excerpt (Optional)</label>
          <textarea
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Brief summary of your blog (auto-generated if left empty)"
            maxLength="300"
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/300 characters</p>
        </div>

        {/* Category & Tags */}
        <div className="bg-white rounded-lg shadow p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="technology">Technology</option>
              <option value="business">Business</option>
              <option value="design">Design</option>
              <option value="marketing">Marketing</option>
              <option value="tutorial">Tutorial</option>
              <option value="news">News</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="react, javascript, tutorial (comma separated)"
            />
          </div>
        </div>

        {/* Featured Image Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium mb-2">Featured Image</label>
          
          {/* Image Preview */}
          {imagePreview && (
            <div className="mb-4 relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-64 rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
              >
                <FaTrash />
              </button>
            </div>
          )}

          {/* Upload Options */}
          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                <FaImage className="inline mr-2" />
                Upload from Computer
              </label>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleImageUpload}
                  disabled={!imageFile || uploading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaImage />
                      Upload
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max size: 5MB | Formats: JPG, PNG, GIF, WebP
              </p>
            </div>

            {/* OR Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* URL Input */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">Enter Image URL</label>
              <input
                type="url"
                value={formData.featuredImage}
                onChange={(e) => {
                  setFormData({ ...formData, featuredImage: e.target.value });
                  setImagePreview(e.target.value);
                }}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
        </div>

        {/* SEO Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">SEO Settings (Optional)</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Meta Description</label>
              <textarea
                value={formData.metaDescription}
                onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="2"
                placeholder="Brief description for search engines"
                maxLength="160"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Meta Keywords</label>
              <input
                type="text"
                value={formData.metaKeywords}
                onChange={(e) => setFormData({ ...formData, metaKeywords: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Blog Settings</h3>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="allowComments"
              checked={formData.allowComments}
              onChange={(e) => setFormData({ ...formData, allowComments: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="allowComments" className="text-sm">Allow comments on this blog</label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={savingDraft}
              className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FaSave />
              {savingDraft ? 'Saving...' : 'Save as Draft'}
            </button>

            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={submitting}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FaPaperPlane />
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">
            Save as draft to continue editing later, or submit for approval when ready to publish
          </p>
        </div>
      </form>
    </div>
  );
};

export default CreateEditBlog;
